import { Container, Text, Graphics } from 'pixi.js';
import { ANIMATION_DURATIONS } from '../config/animationConfig.js';

/**
 * ToastManager class handles the display of temporary notification messages
 * overlaid on the game canvas.
 * 
 * Features:
 * - Shows toast messages with automatic fade-out
 * - Manages multiple toasts in a queue
 * - Customizable position, duration, and styling
 */
class ToastManager {
    constructor(app) {
        this.app = app;
        this.container = null;
        this.activeToasts = [];
        this.toastQueue = [];
        this.maxSimultaneousToasts = 3;
        this.defaultDuration = 3000; // 3 seconds
        this.toastSpacing = 10;
        this.initialize();
    }

    /**
     * Initialize the toast container
     */
    initialize() {
        this.container = new Container();
        this.container.label = 'ToastContainer';
        this.container.zIndex = 1000; // Ensure toasts appear on top
        this.app.stage.addChild(this.container);
        this.app.stage.sortableChildren = true;
    }

    /**
     * Show a toast message
     * @param {string} message - The message to display
     * @param {Object} options - Configuration options
     * @param {number} options.duration - How long to show the toast (ms)
     * @param {string} options.type - Toast type: 'info', 'warning', 'error', 'success'
     * @param {string} options.position - Position: 'top', 'center', 'bottom'
     */
    showToast(message, options = {}) {
        const config = {
            duration: options.duration || this.defaultDuration,
            type: options.type || 'info',
            position: options.position || 'top',
            ...options
        };

        // If we have too many active toasts, queue this one
        if (this.activeToasts.length >= this.maxSimultaneousToasts) {
            this.toastQueue.push({ message, config });
            return;
        }

        this.createToast(message, config);
    }

    /**
     * Create and display a toast element
     */
    createToast(message, config) {
        if (!this.app || !this.app.renderer || !this.app.renderer.screen) {
            console.warn('[ToastManager] Cannot create toast - app or renderer is null');
            return null;
        }

        const toast = new Container();
        toast.label = 'Toast';

        // Create background
        const background = new Graphics();
        const backgroundColor = this.getBackgroundColor(config.type, config.teamColor);
        const padding = 20;
        const maxWidth = this.app.screen.width * 0.8;

        // Create text - support multi-line with \n
        const textStyle = {
            fontFamily: 'Arial, sans-serif',
            fontSize: 18,
            fill: 0xFFFFFF,
            align: 'center',
            wordWrap: true,
            wordWrapWidth: maxWidth - padding * 2,
            fontWeight: 'bold',
            lineHeight: 24 // Better spacing for multi-line
        };

        const text = new Text({ text: message, style: textStyle });
        text.anchor.set(0.5);

        // Draw background with rounded corners and subtle shadow effect
        const bgWidth = Math.min(text.width + padding * 2, maxWidth);
        const bgHeight = text.height + padding * 2;

        background.roundRect(
            -bgWidth / 2,
            -bgHeight / 2,
            bgWidth,
            bgHeight,
            10
        );
        background.fill({ color: backgroundColor, alpha: 0.95 });

        // Add border for emphasis
        background.roundRect(
            -bgWidth / 2,
            -bgHeight / 2,
            bgWidth,
            bgHeight,
            10
        );
        background.stroke({ width: 2, color: 0xFFFFFF, alpha: 0.3 });

        toast.addChild(background);
        toast.addChild(text);

        // Position toast
        this.positionToast(toast, config.position, bgHeight);

        // Check if positioning failed (app was destroyed)
        if (!this.app || !this.app.renderer || !this.app.renderer.screen) {
            return null;
        }

        // Initial alpha for fade-in
        toast.alpha = 0;

        this.container.addChild(toast);
        this.activeToasts.push({ container: toast, config, bgHeight });

        // Animate toast
        this.animateToast(toast, config);

        return toast;
    }

    /**
     * Position toast based on configuration
     */
    positionToast(toast, position, height) {
        if (!this.app || !this.app.renderer || !this.app.renderer.screen) {
            console.warn('[ToastManager] Cannot position toast - app or renderer is null');
            return;
        }

        const screenWidth = this.app.screen.width;
        const screenHeight = this.app.screen.height;

        toast.x = screenWidth / 2;

        // Calculate offset based on existing toasts
        const offset = this.activeToasts.reduce((sum, t) =>
            sum + t.bgHeight + this.toastSpacing, 0);

        switch (position) {
            case 'top':
                toast.y = 100 + offset;
                break;
            case 'center':
                toast.y = screenHeight / 2 + offset;
                break;
            case 'bottom':
                toast.y = screenHeight - 100 - offset;
                break;
            default:
                toast.y = 100 + offset;
        }
    }

    /**
     * Animate toast fade-in and fade-out
     */
    animateToast(toast, config) {
        const fadeInDuration = ANIMATION_DURATIONS.TOAST_FADE_IN;
        const fadeOutDuration = ANIMATION_DURATIONS.TOAST_FADE_OUT;
        const displayDuration = config.duration - fadeInDuration - fadeOutDuration;

        let elapsed = 0;
        let phase = 'fadeIn'; // fadeIn -> display -> fadeOut

        const animate = (ticker) => {
            // Safety check: stop animation if app or renderer becomes null
            if (!this.app || !this.app.renderer || !this.app.renderer.screen) {
                if (this.app && this.app.ticker) {
                    this.app.ticker.remove(animate);
                }
                return;
            }

            elapsed += ticker.deltaMS; // Use deltaMS for time in milliseconds

            if (phase === 'fadeIn') {
                toast.alpha = Math.min(1, elapsed / fadeInDuration);
                if (elapsed >= fadeInDuration) {
                    phase = 'display';
                    elapsed = 0;
                }
            } else if (phase === 'display') {
                if (elapsed >= displayDuration) {
                    phase = 'fadeOut';
                    elapsed = 0;
                }
            } else if (phase === 'fadeOut') {
                toast.alpha = Math.max(0, 1 - elapsed / fadeOutDuration);
                if (elapsed >= fadeOutDuration) {
                    this.removeToast(toast, animate);
                }
            }
        };

        this.app.ticker.add(animate);
    }

    /**
     * Remove toast from display and show next queued toast
     */
    removeToast(toast, animateFn) {
        // Safety check before removing ticker
        if (this.app && this.app.ticker) {
            this.app.ticker.remove(animateFn);
        }

        if (this.container && toast && !toast.destroyed && toast.parent === this.container) {
            this.container.removeChild(toast);
        }
        if (toast && !toast.destroyed) {
            toast.destroy();
        }

        // Remove from active toasts
        const index = this.activeToasts.findIndex(t => t.container === toast);
        if (index !== -1) {
            this.activeToasts.splice(index, 1);
        }

        // Show next queued toast if any
        if (this.toastQueue.length > 0 && this.app && this.app.renderer && this.app.renderer.screen) {
            const { message, config } = this.toastQueue.shift();
            this.createToast(message, config);
        }
    }

    /**
     * Get background color based on toast type or custom color
     * @param {string} type - Toast type or team identifier
     * @param {number} customColor - Optional custom hex color
     */
    getBackgroundColor(type, customColor = null) {
        // If custom color is provided, use it
        if (customColor !== null) {
            return customColor;
        }

        const colors = {
            info: 0x2196F3,    // Blue
            success: 0x4CAF50, // Green
            warning: 0xFF9800, // Orange
            error: 0xF44336,   // Red
            team1: 0x667eea,   // Team 1 - Blue
            team2: 0xf5576c    // Team 2 - Red/Pink
        };
        return colors[type] || colors.info;
    }

    /**
     * Show a toast positioned near a specific player
     * @param {string} message - The message to display
     * @param {number} playerIndex - Player index (0=bottom, 1=right, 2=top, 3=left)
     * @param {Object} options - Configuration options
     */
    showPlayerToast(message, playerIndex, options = {}) {
        if (!this.app || !this.app.renderer || !this.app.renderer.screen) {
            console.warn('[ToastManager] Cannot show player toast - app or renderer is null');
            return;
        }

        const config = {
            duration: options.duration || 3500,
            type: options.type || 'info',
            ...options
        };

        const toast = new Container();
        toast.label = 'PlayerToast';

        // Create background - use team color if provided
        const backgroundColor = this.getBackgroundColor(config.type, config.teamColor);
        const padding = 12;
        const maxWidth = 220; // Slightly wider for better icon display

        // Create text with smaller font for player toasts - support multi-line
        const textStyle = {
            fontFamily: 'Arial, sans-serif',
            fontSize: 16,
            fill: 0xFFFFFF,
            align: 'center',
            wordWrap: true,
            wordWrapWidth: maxWidth - padding * 2,
            fontWeight: 'bold',
            lineHeight: 20 // Better spacing for multi-line
        };

        const text = new Text({ text: message, style: textStyle });
        text.anchor.set(0.5);

        // Draw background with rounded corners
        const bgWidth = Math.min(text.width + padding * 2, maxWidth);
        const bgHeight = text.height + padding * 2;

        const background = new Graphics();
        background.roundRect(
            -bgWidth / 2,
            -bgHeight / 2,
            bgWidth,
            bgHeight,
            8
        );
        background.fill({ color: backgroundColor, alpha: 0.95 }); // Slightly more opaque for better visibility

        // Add border
        background.roundRect(
            -bgWidth / 2,
            -bgHeight / 2,
            bgWidth,
            bgHeight,
            8
        );
        background.stroke({ width: 2, color: 0xFFFFFF, alpha: 0.4 });

        toast.addChild(background);
        toast.addChild(text);

        // Position toast based on player position
        // Match the card positions from DealManagerRenderer
        const screenWidth = this.app.screen.width;
        const screenHeight = this.app.screen.height;
        const centerX = screenWidth / 2;
        const centerY = screenHeight / 2;

        // Estimate card dimensions (same as DealManagerRenderer)
        const cardWidth = screenWidth * 0.08;
        const cardHeight = cardWidth * 1.5;
        const cardMargin = cardHeight * 0.6; // Same margin used for cards

        switch (playerIndex) {
            case 0: // Bottom player (human) - show above their cards
                toast.x = centerX;
                toast.y = screenHeight - cardMargin - cardHeight - 20; // Above the cards
                break;
            case 1: // Right player - show on their cards area
                toast.x = screenWidth - cardMargin - cardHeight / 2; // Cards are rotated, so use cardHeight for x offset
                toast.y = centerY;
                break;
            case 2: // Top player - show below their cards but higher up
                toast.x = centerX;
                // Cards are centered at y = cardMargin (see DealManagerRenderer).
                // Position the toast just below the bottom edge of the top cards.
                toast.y = (cardMargin + cardHeight / 2) + 8 + (bgHeight / 2);
                break;
            case 3: // Left player - show on their cards area
                toast.x = cardMargin + cardHeight / 2; // Cards are rotated
                toast.y = centerY;
                break;
            default:
                toast.x = centerX;
                toast.y = centerY;
        }

        // Initial alpha for fade-in
        toast.alpha = 0;
        toast.zIndex = 1001; // Above regular toasts

        this.container.addChild(toast);

        // Animate with fade-in and fade-out
        this.animatePlayerToast(toast, config);
    }

    /**
     * Animate player toast fade-in and fade-out
     */
    animatePlayerToast(toast, config) {
        const fadeInDuration = Math.round(ANIMATION_DURATIONS.TOAST_FADE_IN * 0.67); // Slightly faster
        const fadeOutDuration = ANIMATION_DURATIONS.TOAST_FADE_OUT;
        const displayDuration = config.duration - fadeInDuration - fadeOutDuration;

        let elapsed = 0;
        let phase = 'fadeIn';

        const animate = (ticker) => {
            // Safety check: stop animation if app or renderer becomes null
            if (!this.app || !this.app.renderer || !this.app.renderer.screen) {
                if (this.app && this.app.ticker) {
                    this.app.ticker.remove(animate);
                }
                if (this.container && toast && !toast.destroyed && toast.parent === this.container) {
                    this.container.removeChild(toast);
                }
                if (toast && !toast.destroyed) {
                    toast.destroy();
                }
                return;
            }

            elapsed += ticker.deltaMS;

            if (phase === 'fadeIn') {
                toast.alpha = Math.min(1, elapsed / fadeInDuration);
                // Slight scale animation for emphasis
                toast.scale.set(0.8 + 0.2 * Math.min(1, elapsed / fadeInDuration));
                if (elapsed >= fadeInDuration) {
                    phase = 'display';
                    elapsed = 0;
                    toast.scale.set(1);
                }
            } else if (phase === 'display') {
                if (elapsed >= displayDuration) {
                    phase = 'fadeOut';
                    elapsed = 0;
                }
            } else if (phase === 'fadeOut') {
                toast.alpha = Math.max(0, 1 - elapsed / fadeOutDuration);
                if (elapsed >= fadeOutDuration) {
                    this.app.ticker.remove(animate);
                    if (this.container && toast && !toast.destroyed && toast.parent === this.container) {
                        this.container.removeChild(toast);
                    }
                    if (toast && !toast.destroyed) {
                        toast.destroy();
                    }
                }
            }
        };

        this.app.ticker.add(animate);
    }

    /**
     * Clear all toasts
     */
    clearAll() {
        if (this.container) {
            this.activeToasts.forEach(({ container }) => {
                if (container && !container.destroyed && container.parent === this.container) {
                    this.container.removeChild(container);
                }
                if (container && !container.destroyed) {
                    container.destroy();
                }
            });
        }
        this.activeToasts = [];
        this.toastQueue = [];
    }

    /**
     * Destroy the toast manager
     */
    destroy() {
        // Stop all active animations by removing ticker listeners
        // Note: We can't explicitly store ticker callbacks, but clearing the container
        // will cause the callbacks to early-exit on their next tick due to validation checks
        this.clearAll();

        // Remove container from stage
        if (this.container && this.app && this.app.stage) {
            try {
                this.app.stage.removeChild(this.container);
                this.container.destroy({ children: true });
            } catch (error) {
                console.warn('[ToastManager] Error destroying container:', error);
            }
        }

        // Clear references
        this.container = null;
        this.app = null;
    }
}

export default ToastManager;
